import { Box, Skeleton, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { CandidateNote } from 'services/candidate/types';
import { CandidateNoteEditor } from './CandidateNoteEditor';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useMutation, useQuery } from 'services/api/hooks';
import {
  createCandidateNote,
  deleteCandidateNote,
  getCandidateNotesByCandidate,
  updateCandidateNote
} from 'services/candidate/api';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { useConfirmationModal } from 'services/modals/hooks';
import { organizationAtom } from 'services/organization/atom';

const container: SxProps = {
  width: '100%',
  pb: 2
};

const buttonContainer: SxProps = {
  backgroundColor: 'customGrey.light',
  color: 'text.primary',
  borderStyle: 'dashed',
  borderColor: 'customGrey.medium',
  borderWidth: '1px',
  mt: 2
};

const noteContainer: SxProps = {
  mb: 2
};

type Props = {
  candidateId: string;
  readOnly?: boolean;
};

type AddCandidateNote = Omit<CandidateNote, 'id'> & { id?: string };

export const CandidateNotesTab: React.FC<Props> = ({
  candidateId,
  readOnly = false
}) => {
  const user = useRecoilValue(userAtom);
  const organization = useRecoilValue(organizationAtom);
  const { showModal } = useConfirmationModal();
  const [candidateNotesAdded, setCandidateNotesAdded] = useState<
    AddCandidateNote[]
  >([]);

  const {
    data: candidateNotes,
    refetch,
    isLoading
  } = useQuery<CandidateNote[]>(
    getCandidateNotesByCandidate,
    {
      candidateId
    },
    {
      onFetchSuccess: () => {
        setCandidateNotesAdded([]);
      }
    }
  );

  const { mutate: createCandidateNoteAction } = useMutation(
    createCandidateNote,
    {
      disableSuccessToast: true
    }
  );

  const { mutate: updateCandidateNoteAction } = useMutation(
    updateCandidateNote,
    {
      disableSuccessToast: true
    }
  );

  const { mutate: deleteCandidateNoteAction } = useMutation(
    deleteCandidateNote,
    {
      disableSuccessToast: true
    }
  );

  const setNoteInList = (candidateNote: CandidateNote, index: number) => {
    const newNotes = [...candidateNotesAdded];
    newNotes[index] = candidateNote;
    setCandidateNotesAdded(newNotes);
  };

  const handleAddNote = () => {
    setCandidateNotesAdded([
      ...candidateNotesAdded,
      {
        content: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: '',
        organizationId: '',
        candidateId
      }
    ]);
  };

  const removeNoteFromList = (id: string) => {
    const newNotes = candidateNotesAdded.filter((note) => note.id !== id);
    setCandidateNotesAdded(newNotes);

    refetch();
  };

  const createNote = async (content: string, index: number) => {
    if (!user?.id || !organization?.id) {
      throw new Error('User not found');
    }

    const candidateNote = await createCandidateNoteAction({
      content,
      userId: user.id,
      organizationId: organization.id,
      candidateId
    });

    setNoteInList(candidateNote, index);
  };

  const updateNote = async (id: string, content: string) => {
    await updateCandidateNoteAction({
      id,
      content
    });
  };

  const onDelete = async (id: string) => {
    showModal({
      title: 'Delete Note',
      message: 'Are you sure you want to delete this note?',
      onConfirm: async () => {
        await deleteNote(id);
      }
    });
  };

  const cancelNoteCreation = (index: number) => {
    const newNotes = candidateNotesAdded.filter((note, i) => i !== index);
    setCandidateNotesAdded(newNotes);
  };

  const deleteNote = async (id: string) => {
    await deleteCandidateNoteAction({ id });
    removeNoteFromList(id);
    refetch();
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" width="100%" height={100} />
      </Box>
    );
  }

  return (
    <Box sx={container}>
      {candidateNotes?.map((note) => (
        <Box key={note.id} sx={noteContainer}>
          <CandidateNoteEditor
            id={note.id}
            content={note.content}
            isInitialEditing={!note.content}
            updateNote={updateNote}
            deleteNote={onDelete}
            readOnly={readOnly}
            createdBy={note.user?.name}
            createdAt={note.createdAt}
          />
        </Box>
      ))}

      {candidateNotesAdded.map((note, index) => (
        <Box sx={noteContainer} key={`note_to_add_${index}`}>
          <CandidateNoteEditor
            id={note.id || undefined}
            content={note.content}
            isInitialEditing={!note.content}
            createNote={(value) => createNote(value, index)}
            updateNote={updateNote}
            createdAt={note.createdAt}
            createdBy={note.user?.name}
            onCancel={() => cancelNoteCreation(index)}
            deleteNote={onDelete}
          />
        </Box>
      ))}

      {!readOnly && (
        <Button
          startIcon={<AddIcon />}
          sx={buttonContainer}
          fullWidth
          onClick={handleAddNote}
        >
          Add comment
        </Button>
      )}
    </Box>
  );
};
